/**
 * DeleteAccount
 * ────────────────────────────────────────────────────────────────────────────
 * The public account deletion page, at /delete-account.
 *
 * This URL is what goes in the Google Play Data safety form for BOTH Android
 * apps (Lusuku and Suku Rider). Play requires it to be reachable with no
 * sign-in, to name the developer and the app, to say what is deleted and what
 * is kept, and to say how long deletion takes. A page that needs a login, or a
 * link that lands on the home page, is recorded as an invalid deletion link.
 *
 * It posts to the same endpoint the apps use in-app,
 * POST /user/request-account-deletion, which accepts an unauthenticated request
 * as long as it carries an email address or a phone number. Nothing is deleted
 * synchronously: a PENDING request is recorded for the ops team's 30 day SLA,
 * so this page must never say the account has been deleted.
 */
import React, { useState } from "react";
import styled from "styled-components";

import { publicRequest } from "../api/requestMethods";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { BRAND_INK, BRAND_GOLD, BRAND_INK_LIFT } from "../config/brandColors";
import { SUPPORT_EMAIL, SUPPORT_PHONE, SUPPORT_TEL } from "../config/market";

const ACCOUNT_TYPES = [
  {
    value: "customer",
    label: "Lusuku",
    hint: "The shopping app you order food, groceries and medicine with",
  },
  {
    value: "rider",
    label: "Suku Rider",
    hint: "The delivery app riders use to take orders",
  },
];

// publicRequest carries the market scope and no credentials, which is exactly
// right here: the page must work for someone who is signed out or who has
// already uninstalled the app.
const ENDPOINT = "/user/request-account-deletion";

/**
 * The form on its own, with no site chrome around it, so the submit rules can
 * be tested without standing up the store's router, redux and maps providers.
 */
export const DeleteAccountForm = () => {
  const [accountType, setAccountType] = useState("customer");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [reason, setReason] = useState("");
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const busy = status.state === "sending";

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;

    if (!email.trim() && !phoneNumber.trim()) {
      setStatus({
        state: "error",
        message:
          "Enter the email address or the phone number on the account, so we know which account to delete.",
      });
      return;
    }

    setStatus({ state: "sending", message: "" });
    try {
      const { data } = await publicRequest.post(ENDPOINT, {
        accountType,
        email: email.trim() || undefined,
        phoneNumber: phoneNumber.trim() || undefined,
        reason: reason.trim() || undefined,
        platform: "web",
      });
      setStatus({
        state: "done",
        message:
          data?.message ||
          "Your deletion request has been received. We will process it within 30 days.",
      });
    } catch (error) {
      const response = error?.response;
      if (response?.status === 409) {
        setStatus({
          state: "done",
          message:
            response.data?.message ||
            "You already have a deletion request with us. It will be processed within 30 days.",
        });
        return;
      }
      setStatus({
        state: "error",
        message:
          response?.data?.message ||
          `We could not submit your request just now. Please try again, or contact us on ${SUPPORT_PHONE}.`,
      });
    }
  };

  return (
    <Card as="form" onSubmit={submit}>
            <FieldSet>
              <Legend>Which account</Legend>
              <Choices>
                {ACCOUNT_TYPES.map((option) => (
                  <Choice
                    key={option.value}
                    $active={accountType === option.value}
                  >
                    <input
                      type="radio"
                      name="accountType"
                      value={option.value}
                      checked={accountType === option.value}
                      onChange={() => setAccountType(option.value)}
                    />
                    <span>
                      <strong>{option.label}</strong>
                      <small>{option.hint}</small>
                    </span>
                  </Choice>
                ))}
              </Choices>
            </FieldSet>

            <FieldSet>
              <Legend>How we find your account</Legend>
              <Hint>
                Give the email address or the phone number you signed up with.
                One of them is enough; both is faster.
              </Hint>
              <Label htmlFor="delete-email">Email address</Label>
              <Input
                id="delete-email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="you@example.com"
              />
              <Label htmlFor="delete-phone">Phone number</Label>
              <Input
                id="delete-phone"
                type="tel"
                autoComplete="tel"
                value={phoneNumber}
                onChange={(event) => setPhoneNumber(event.target.value)}
                placeholder="0700 000 000"
              />
              <Label htmlFor="delete-reason">
                Reason for leaving (optional)
              </Label>
              <TextArea
                id="delete-reason"
                rows={3}
                maxLength={1000}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Anything you would like us to know"
              />
            </FieldSet>

            {status.state === "error" && <Alert role="alert">{status.message}</Alert>}
            {status.state === "done" && (
              <Success role="status">
                <strong>Request received</strong>
                <span>{status.message}</span>
              </Success>
            )}

            {status.state !== "done" && (
              <Submit type="submit" disabled={busy}>
                {busy ? "Sending your request" : "Request account deletion"}
              </Submit>
            )}
    </Card>
  );
};

const DeleteAccount = () => (
  <>
    <Navbar />
    <Page>
      <Shell>
        <Header>
          <Eyebrow>Lusuku Fresh Services Limited</Eyebrow>
          <h1>Delete your account</h1>
          <p>
            This page deletes an account in the Lusuku app or the Suku Rider
            app. You do not need to sign in to use it.
          </p>
        </Header>

        <DeleteAccountForm />

          <Card>
            <h2>You can also do this inside the app</h2>
            <ol>
              <li>
                <strong>Lusuku:</strong> open Profile, then Delete account.
              </li>
              <li>
                <strong>Suku Rider:</strong> open the menu, then Settings, then
                Delete account.
              </li>
            </ol>
          </Card>

          <Card>
            <h2>What happens next</h2>
            <ol>
              <li>
                We record your request and send a confirmation to the email
                address on the account.
              </li>
              <li>
                We check that the request comes from the account holder, and we
                may call or message you on the number on the account.
              </li>
              <li>
                We complete the deletion within 30 days. If an order or a payment
                is still in progress, we finish or refund it first.
              </li>
            </ol>
          </Card>

          <Card>
            <h2>What is deleted</h2>
            <ul>
              <li>Your profile: name, email address, phone number and password.</li>
              <li>Your saved delivery addresses and saved locations.</li>
              <li>Your cart, saved items and product reviews.</li>
              <li>
                Your device push tokens, so the apps stop sending you
                notifications.
              </li>
              <li>
                For riders: your rider profile, vehicle details and shift history.
              </li>
            </ul>

            <h2>What we have to keep, and for how long</h2>
            <p>
              Ugandan tax law requires us to keep records of completed sales.
              Orders, invoices, receipts and the fiscal records filed with the
              Uganda Revenue Authority are kept for the statutory retention
              period and are not deleted with your account. The same applies to
              rider payout records, which are accounting records.
            </p>
            <p>
              Those records are detached from your profile where the law allows,
              so they are held as transaction history rather than as a customer
              or rider you can be contacted through.
            </p>
          </Card>

          <Card>
            <h2>Need help</h2>
            <p>
              Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> or
              call <a href={`tel:${SUPPORT_TEL}`}>{SUPPORT_PHONE}</a>.
            </p>
          </Card>
      </Shell>
    </Page>
    <Footer />
  </>
);

const Page = styled.main`
  background: #f6f7f7;
  padding: 32px 16px 64px;
  min-height: 70vh;
`;

const Shell = styled.div`
  max-width: 720px;
  margin: 0 auto;
`;

const Header = styled.header`
  background: ${BRAND_INK};
  color: #fff;
  border-radius: 14px;
  padding: 28px 26px;
  margin-bottom: 18px;

  h1 {
    margin: 6px 0 10px;
    font-size: 28px;
    font-weight: 800;
    line-height: 1.15;
  }

  p {
    margin: 0;
    color: #d8e0e1;
    font-size: 15px;
    line-height: 1.6;
  }
`;

const Eyebrow = styled.div`
  color: ${BRAND_GOLD};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const Card = styled.section`
  background: #fff;
  border: 1px solid #e6e9e9;
  border-radius: 14px;
  padding: 22px 24px;
  margin-bottom: 16px;

  h2 {
    font-size: 17px;
    font-weight: 700;
    color: ${BRAND_INK};
    margin: 0 0 12px;
  }

  h2 + h2 {
    margin-top: 22px;
  }

  p,
  li {
    color: #44514f;
    font-size: 15px;
    line-height: 1.65;
  }

  ul,
  ol {
    margin: 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 8px;
  }

  a {
    color: ${BRAND_INK_LIFT};
    font-weight: 600;
  }
`;

const FieldSet = styled.fieldset`
  border: 0;
  margin: 0 0 18px;
  padding: 0;
`;

const Legend = styled.legend`
  font-size: 17px;
  font-weight: 700;
  color: ${BRAND_INK};
  margin-bottom: 10px;
  padding: 0;
`;

const Hint = styled.p`
  margin: 0 0 14px;
  color: #6a7674;
  font-size: 14px;
  line-height: 1.6;
`;

const Choices = styled.div`
  display: grid;
  gap: 10px;
`;

const Choice = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border: 2px solid ${(props) => (props.$active ? BRAND_INK : "#e3e7e7")};
  background: ${(props) => (props.$active ? "#f2f6f6" : "#fff")};
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;

  input {
    margin-top: 3px;
    accent-color: ${BRAND_INK};
    width: 18px;
    height: 18px;
  }

  span {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  strong {
    color: ${BRAND_INK};
    font-size: 15px;
  }

  small {
    color: #6a7674;
    font-size: 13px;
    line-height: 1.45;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: ${BRAND_INK};
  margin: 14px 0 6px;
`;

const fieldStyles = `
  width: 100%;
  border: 1px solid #d6dcdc;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 15px;
  color: #1d2a29;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #142427;
    box-shadow: 0 0 0 3px rgba(20, 36, 39, 0.12);
  }
`;

const Input = styled.input`
  ${fieldStyles}
`;

const TextArea = styled.textarea`
  ${fieldStyles}
  resize: vertical;
`;

const Submit = styled.button`
  width: 100%;
  border: 0;
  border-radius: 12px;
  background: #b3261e;
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  padding: 15px 18px;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const Alert = styled.div`
  background: #fdecea;
  border: 1px solid #f5c2bd;
  color: #8c1d16;
  border-radius: 10px;
  padding: 12px 14px;
  font-size: 14px;
  line-height: 1.55;
  margin-bottom: 14px;
`;

const Success = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: #edf7ee;
  border: 1px solid #bfe0c3;
  border-radius: 10px;
  padding: 14px 16px;
  color: #1e5128;
  font-size: 15px;
  line-height: 1.55;

  strong {
    font-size: 15px;
  }
`;

export default DeleteAccount;
